import { useEffect, useRef, useState } from "react";
import * as pdfjsLib from "pdfjs-dist";
import pdfjsWorker from "pdfjs-dist/build/pdf.worker.min.mjs?url";
import { Button } from "@component/button";
import {
    ChevronLeftIcon, ChevronRightIcon, ZoomInIcon, ZoomOutIcon, Loader2Icon
} from "lucide-react";
import { cn } from "@util/tailwind";

// Set worker source for Vite
pdfjsLib.GlobalWorkerOptions.workerSrc = pdfjsWorker;

interface PDFViewerProps {
    url: string;
    className?: string;
    defaultZoom?: number
}

export function PDFViewer({ url, className, defaultZoom = 1.2 }: PDFViewerProps) {
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const [pdfDoc, setPdfDoc] = useState<pdfjsLib.PDFDocumentProxy | null>(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(0);
    const [scale, setScale] = useState(defaultZoom);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    // Load PDF document
    useEffect(() => {
        if (!url) {
            setError("No PDF URL provided");
            setIsLoading(false);
            return;
        }

        setIsLoading(true);
        setError(null);

        const loadPdf = async () => {
            try {
                // Fetch PDF as blob first to avoid CORS issues
                const response = await fetch(url);
                if (!response.ok) {
                    throw new Error(`Failed to fetch PDF: ${response.statusText}`);
                }

                const blob = await response.blob();
                const arrayBuffer = await blob.arrayBuffer();

                // Load PDF from array buffer
                const loadingTask = pdfjsLib.getDocument({ data: arrayBuffer });
                const pdf = await loadingTask.promise;
                setPdfDoc(pdf);
                setTotalPages(pdf.numPages);
                setCurrentPage(1);
            } catch (err) {
                console.error("Error loading PDF:", err);
                setError("Failed to load PDF. Please try again.");
            } finally {
                setIsLoading(false);
            }
        };

        loadPdf();

        return () => {
            if (pdfDoc) {
                pdfDoc.destroy();
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [url]);

    // Render page
    useEffect(() => {
        if (!pdfDoc || !canvasRef.current) return;

        const renderPage = async () => {
            try {
                const page = await pdfDoc.getPage(currentPage);
                const viewport = page.getViewport({ scale });

                const canvas = canvasRef.current;
                if (!canvas) return;

                const context = canvas.getContext("2d");
                if (!context) return;

                canvas.height = viewport.height;
                canvas.width = viewport.width;

                const renderContext = {
                    canvasContext: context,
                    viewport: viewport,
                    canvas: canvas,
                };

                await page.render(renderContext).promise;
            } catch (err) {
                console.error("Error rendering page:", err);
            }
        };

        renderPage();
    }, [pdfDoc, currentPage, scale]);

    const goToPrevPage = () => {
        if (currentPage > 1) {
            setCurrentPage(currentPage - 1);
        }
    };

    const goToNextPage = () => {
        if (currentPage < totalPages) {
            setCurrentPage(currentPage + 1);
        }
    };

    const zoomIn = () => {
        setScale((prev) => Math.min(prev + 0.1, 3));
    };

    const zoomOut = () => {
        setScale((prev) => Math.max(prev - 0.1, 0.1));
    };

    if (isLoading) {
        return (
            <div className={cn("flex items-center justify-center h-full bg-gray-100 dark:bg-gray-800 rounded-lg", className)}>
                <div className="flex flex-col items-center gap-2">
                    <Loader2Icon className="h-8 w-8 animate-spin text-blue-600" />
                    <span className="text-sm text-gray-600 dark:text-gray-400">Loading PDF...</span>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className={cn("flex items-center justify-center h-full bg-gray-100 dark:bg-gray-800 rounded-lg", className)}>
                <div className="text-center">
                    <p className="text-sm text-red-600 dark:text-red-400 mb-2">{error}</p>
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => window.open(url, "_blank")}
                    >
                        Open in new tab
                    </Button>
                </div>
            </div>
        );
    }

    return (
        <div className={cn("flex flex-col h-full", className)}>
            {/* Controls */}
            <div className="flex items-center justify-between px-4 py-2 bg-gray-100 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 rounded-t-lg">
                <div className="flex items-center gap-2">
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={goToPrevPage}
                        disabled={currentPage <= 1}
                        className="h-8 w-8 p-0"
                    >
                        <ChevronLeftIcon className="h-4 w-4" />
                    </Button>
                    <span className="text-sm text-gray-700 dark:text-gray-300 min-w-[100px] text-center">
                        Page {currentPage} of {totalPages}
                    </span>
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={goToNextPage}
                        disabled={currentPage >= totalPages}
                        className="h-8 w-8 p-0"
                    >
                        <ChevronRightIcon className="h-4 w-4" />
                    </Button>
                </div>
                <div className="flex items-center gap-2">
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={zoomOut}
                        disabled={scale <= 0.1}
                        className="h-8 w-8 p-0"
                    >
                        <ZoomOutIcon className="h-4 w-4" />
                    </Button>
                    <span className="text-sm text-gray-700 dark:text-gray-300 min-w-[50px] text-center">
                        {Math.round(scale * 100)}%
                    </span>
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={zoomIn}
                        disabled={scale >= 3}
                        className="h-8 w-8 p-0"
                    >
                        <ZoomInIcon className="h-4 w-4" />
                    </Button>
                </div>
            </div>

            {/* PDF Canvas */}
            <div className="flex-1 overflow-auto bg-gray-200 dark:bg-gray-900 rounded-b-lg">
                <div className="flex justify-center p-4">
                    <canvas
                        ref={canvasRef}
                        className="shadow-lg bg-white"
                    />
                </div>
            </div>
        </div>
    );
}